/**
 * @file        leadAttribution.ts
 * @description Browser-side traffic attribution for leads: remembers the
 *              UTM tags and the external referrer HOST of the visit that
 *              brought the visitor, and hands them to LeadForm at submit.
 * @universal   leadAttribution (knowledge/universals/tools.md)
 * @runs        Browser (SSR-safe: every function checks `typeof window`)
 * @rule        Model = «last non-direct visit», like Yandex Metrika's
 *              default: a landing with UTM tags or an external referrer
 *              overwrites the stored touch; a direct or internal visit
 *              keeps the previous one. A touch older than 30 days is
 *              dropped.
 * @rule        Referrer = HOSTNAME ONLY. The full referrer URL may carry
 *              search queries or personal data and must not cross the
 *              network (same reasoning as pagePath, ADR N-LEAD-PAGEPATH-1).
 *              The server sanitizes again — neither side relies on the other.
 * @rule        Fail-open: storage blocked, quota full or broken JSON means
 *              «no attribution», never a thrown error — a lead must not be
 *              lost to protect an analytics field.
 */

const STORAGE_KEY = 'jckauto:attribution';
const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;
export const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'] as const;

export interface LeadAttribution {
  utm?: Record<string, string>;
  referrer?: string;
}

interface StoredTouch extends LeadAttribution {
  at: number;
}

/**
 * Record the current landing if it carries UTM tags or an external referrer.
 * Called once per full page load from LeadAttributionCapture (root layout).
 * @input  none (reads window.location and document.referrer)
 * @output none
 * @important Only the first page of a visit has a meaningful document.referrer;
 *            client-side navigations keep the value of the landing.
 */
export function captureLandingAttribution(): void {
  if (typeof window === 'undefined') return;
  try {
    const params = new URLSearchParams(window.location.search);
    const utm: Record<string, string> = {};
    for (const key of UTM_KEYS) {
      const v = params.get(key);
      if (v) utm[key] = v.slice(0, 100);
    }
    const referrer = externalReferrerHost();
    if (!Object.keys(utm).length && !referrer) return;
    const touch: StoredTouch = {
      at: Date.now(),
      ...(Object.keys(utm).length ? { utm } : {}),
      ...(referrer ? { referrer } : {}),
    };
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(touch));
  } catch {
    // fail-open — see @rule
  }
}

/**
 * Attribution to attach to a lead POST body.
 * @input  none
 * @output `{ utm?, referrer? }`; an empty object when nothing is known
 * @important Spread straight into the body: absent keys stay absent, so the
 *            payload of a visitor without attribution is unchanged.
 */
export function readLeadAttribution(): LeadAttribution {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const t = JSON.parse(raw) as StoredTouch;
    if (!t || typeof t.at !== 'number' || Date.now() - t.at > MAX_AGE_MS) return {};
    return {
      ...(t.utm ? { utm: t.utm } : {}),
      ...(t.referrer ? { referrer: t.referrer } : {}),
    };
  } catch {
    return {};
  }
}

function externalReferrerHost(): string | undefined {
  if (!document.referrer) return undefined;
  try {
    const host = new URL(document.referrer).hostname;
    if (!host || host === window.location.hostname) return undefined;
    return host;
  } catch {
    return undefined;
  }
}
